/**
 * The heavy layers: the planet, the coastline, every town, the glow of the
 * big cities, the settled lights of the globe, the supernova's particles and
 * the film grain. Each runs on the GPU as whole buffers, moved only by a
 * handful of uniforms per frame; the particles' paths are closed-form, so the
 * GPU computes them too.
 */
import { RAD } from '../data/geo'
import type { Gpu, Program, Viewport } from './gpu'
import { TRAIL_SAMPLES, type GlobeResources, type ParticleResources } from './resources'

/* =====================================================================
   TWEAKABLES: how the layers look.
   ===================================================================== */
/* the land: a faint fill and coastline under the city lights */
const LAND_COL: Rgb = [32, 32, 32] /* its colour */
const LAND_INTENSITY = 1.0 /* how strongly that colour shows: multiplies both alphas below (0 = hidden, 2 = twice as strong) */
const LAND_ALPHA = 0.2 /* alpha of the fill */
const LAND_EDGE = 0.1 /* alpha of the coastline (0 = no outline) */
/* the backlight: the crescent and its atmosphere */
const CRESCENT_SPAN = 105 /* how far round the limb the rim of atmosphere reaches, each way (deg) */
const RIM_WARM: Rgb = [255, 206, 150] /* the atmosphere near the sun */
const RIM_COOL: Rgb = [150, 182, 232] /* ...and far from it: the blue of the photo */
const ATMO_BLUE = 0.8 /* how much of that blue the atmosphere takes on: 0 = all warm gold, 1 = full blue */
const SURF_COL: Rgb = [255, 196, 128] /* the sunlit surface inside the crescent */
const SURF_A = 0.7 /* brightness of the sunlit surface */
const ATMO_A = 1.0 /* brightness of the atmosphere */
const TERMINATOR_SOFT = 0.22 /* how soft the day/night edge is (higher = longer fade) */
const ATMO_IN = 0.035 /* how far the atmosphere's glow reaches in over the disc (× radius) */
const ATMO_OUT = 0.018 /* ...and out above the limb */
const ATMO_HALO = 0.08 /* ...plus a faint, wider halo */
/* the lights */
const DOT_SIZE = 1.0 /* size of every particle and light */
/* colours: white-hot, star gold, gold, bronze, and the sodium amber of city light */
const COLS: Rgb[] = [
  [255, 250, 240],
  [250, 223, 172],
  [236, 194, 126],
  [201, 164, 112],
  [255, 206, 138],
]
/* the boost round active places, and the shade towards the copy */
const NEAR_SIGMA = 0.07 /* how wide the boost is (rad; 0.07 ≈ 450 km) */
const SHADE_A = 0.85 /* how far the lights fade under the copy (0 = not at all, 1 = out) */
const SHADE_SPAN = 0.25 /* how wide the fade is before the point where they are out, × screen width */
const SHADE_CURVE = 1.6 /* how it builds: 1 = evenly, higher = they stay bright longer and fade late */
/* ===================================================================== */

type Rgb = [r: number, g: number, b: number]

/** A camera on the globe: its centre on screen and radius (px), distance (in radii), longitude and tilt (rad). */
export interface View {
  readonly cx: number
  readonly cy: number
  readonly Rs: number
  /** Written by the camera while it works a frame's view out (the hop, the hover turn), and by the loop (the mouse's parallax). */
  d: number
  lam: number
  tilt: number
}

/**
 * Where the backlight comes from: its direction on screen (`lx`, `ly`), and
 * the cosine and sine of how far behind the planet it sits (`ce`, `se`).
 */
export interface Light {
  readonly lx: number
  readonly ly: number
  readonly ce: number
  readonly se: number
}

/** The planet's look this frame, each 0..1. */
export interface PlanetLook {
  readonly disc: number
  readonly land: number
  readonly rim: number
  /** The rim lit all round, while the sun is straight behind. */
  readonly ring?: number
  /** Up to two crescents: the angle (rad) the light comes from, and its strength. */
  readonly lights?: [angle: number, strength: number][]
}

/** How the intro moves the globe and the shell, from the intro's tuning. */
export interface Motion {
  /** The globe's radius as it forms, and after the camera has moved in (× min(width, height)). */
  readonly r0: number
  readonly r1: number
  /** The north pole tipped towards us (rad). */
  readonly tilt: number
  /** The longitude facing us when the camera has moved in (deg), and the turning speed (deg/s). */
  readonly lamEnd: number
  readonly spin: number
  /** When the camera moves in (s after the burst). */
  readonly dolly: readonly [start: number, end: number]
  /** How explosive the shell's flight is. */
  readonly expandPow: number
  /** How visible the lights on the far side are. */
  readonly backA: number
}

/** Everything a layer reads about the frame, as values. */
export interface FrameState {
  readonly viewport: Viewport
  readonly light: Light
  /** Per active place, its point on the unit globe and boost; up to five, x y z w in turn. */
  readonly near: Float32Array
  /** The shade: its strength and side (+ right, - left), and where on screen (px) the lights are out. */
  readonly shade: { w: number; x: number }
}

export function createLayers(gpu: Gpu, motion: Motion) {
  const gl = gpu.gl

  return {
    /** The planet: its dark disc, the land, the crescents and the rims, then the coastline over it. */
    planet(globe: GlobeResources, v: View, look: PlanetLook, frame: FrameState) {
      let program = gpu.program('planet')
      function u(name: string) {
        return gpu.uniform(program, name)
      }
      gpu.use(program, frame.viewport)
      gpu.blend('over')
      camera(program, v, frame)
      gpu.bind(program, [['aQ', globe.quad, 2, 2, 0]])
      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(gl.TEXTURE_2D, globe.land)
      gl.uniform1i(u('uLand'), 0)
      gl.uniform1f(u('uDisc'), look.disc)
      gl.uniform1f(u('uLandA'), Math.min(1, LAND_ALPHA * LAND_INTENSITY * look.land))
      gl.uniform3fv(u('uLandCol'), unit(LAND_COL))
      gl.uniform1f(u('uRim'), look.rim)
      gl.uniform1f(u('uRing'), look.ring || 0)
      const [l0, l1] = look.lights ?? []
      for (const [name, l] of [
        ['uCr0', l0],
        ['uCr1', l1],
      ] as const)
        gl.uniform4f(
          u(name),
          l ? Math.cos(l[0]) : 1,
          l ? Math.sin(l[0]) : 0,
          l ? Math.min(l[1], 1) : 0,
          0
        )
      gl.uniform4f(u('uBl0'), CRESCENT_SPAN * RAD, ATMO_IN, ATMO_OUT, ATMO_HALO)
      gl.uniform4f(u('uBl1'), TERMINATOR_SOFT, SURF_A, ATMO_A, 0)
      const warm = unit(RIM_WARM)
      const cool = unit(RIM_COOL)
      gl.uniform3fv(u('uW1'), warm)
      gl.uniform3fv(
        u('uC1'),
        warm.map((c, i) => c + (cool[i] - c) * ATMO_BLUE)
      )
      gl.uniform3fv(u('uS1'), unit(SURF_COL))
      gl.drawArrays(gl.TRIANGLES, 0, 6)

      // the coastline over it
      const edge = LAND_EDGE * LAND_INTENSITY * look.land * Math.min(look.disc, 1)
      if (edge <= 0.003) return
      program = gpu.program('coast')
      gpu.use(program, frame.viewport)
      camera(program, v, frame)
      gpu.bind(program, [
        ['aT', globe.segment, 2, 2, 0],
        ['aP0', globe.coast, 3, 6, 0, 1],
        ['aP1', globe.coast, 3, 6, 3, 1],
      ])
      const [r, g, b] = unit(LAND_COL)
      const a = Math.min(1, edge)
      gl.uniform4f(u('uCol'), r * a, g * a, b * a, a)
      gl.uniform1f(u('uHw'), Math.max(0.6, sizeOf(frame.viewport) * 0.0007) / 2)
      gpu.drawInstanced(6, globe.coastSegments)
    },

    /** Every town, a small round light. */
    towns(globe: GlobeResources, v: View, alpha: number, dayK: number, frame: FrameState) {
      const program = gpu.program('towns')
      function u(name: string) {
        return gpu.uniform(program, name)
      }
      const size = sizeOf(frame.viewport)
      gpu.use(program, frame.viewport)
      gpu.blend('add')
      camera(program, v, frame)
      gpu.bind(program, [
        ['aQ', globe.quad, 2, 2, 0],
        ['aP', globe.towns, 4, 4, 0, 1],
      ])
      gl.uniform1f(u('uA'), alpha)
      gl.uniform1f(u('uDayK'), dayK)
      gl.uniform1f(u('uTr'), size * 0.0007 * DOT_SIZE)
      gl.uniform1f(u('uRref'), motion.r0 * size)
      gl.uniform3f(u('uCol'), COLS[4][0] / 255, COLS[4][1] / 255, COLS[4][2] / 255)
      gpu.drawInstanced(6, globe.townCount)
    },

    /** The soft glow round the largest cities. */
    metros(globe: GlobeResources, v: View, alpha: number, dayK: number, frame: FrameState) {
      const program = gpu.program('metros')
      function u(name: string) {
        return gpu.uniform(program, name)
      }
      gpu.use(program, frame.viewport)
      gpu.blend('add')
      camera(program, v, frame)
      gpu.bind(program, [
        ['aQ', globe.quad, 2, 2, 0],
        ['aP', globe.metros, 4, 4, 0, 1],
      ])
      gl.uniform1f(u('uA'), alpha)
      gl.uniform1f(u('uDayK'), dayK)
      gl.uniform1f(u('uRref'), motion.r0 * sizeOf(frame.viewport))
      gl.uniform1f(u('uDot'), DOT_SIZE)
      gpu.drawInstanced(6, globe.metroCount)
    },

    /** The lights where the supernova set them down. */
    lights(
      globe: GlobeResources,
      particles: ParticleResources,
      count: number,
      v: View,
      tau: number,
      dayK: number,
      backK: number,
      frame: FrameState
    ) {
      const program = gpu.program('lights')
      function u(name: string) {
        return gpu.uniform(program, name)
      }
      const size = sizeOf(frame.viewport)
      gpu.use(program, frame.viewport)
      gpu.blend('add')
      camera(program, v, frame)
      gpu.bind(program, [
        ['aQ', globe.quad, 2, 2, 0],
        ['aP', particles.lights, 3, 7, 0, 1],
        ['aD', particles.lights, 4, 7, 3, 1],
      ])
      gl.uniform1f(u('uTau'), tau)
      gl.uniform1f(u('uDayK'), dayK)
      gl.uniform1f(u('uBackK'), backK)
      gl.uniform1f(u('uDot'), size * 0.0009 * DOT_SIZE)
      gl.uniform1f(u('uRref'), motion.r0 * size)
      for (const i of [0, 2, 4])
        gl.uniform3f(u(`uCol${i}`), COLS[i][0] / 255, COLS[i][1] / 255, COLS[i][2] / 255)
      gpu.drawInstanced(6, count)
    },

    /** The supernova: every particle and its trail. */
    sparks(
      globe: GlobeResources,
      particles: ParticleResources,
      count: number,
      tau: number,
      trail: number,
      dayK: number,
      frame: FrameState
    ) {
      const program = gpu.program('sparks')
      function u(name: string) {
        return gpu.uniform(program, name)
      }
      const { viewport, light } = frame
      gpu.use(program, viewport)
      gpu.blend('add')
      gpu.bind(program, [
        ['aT', globe.ribbon, 4, 4, 0],
        ['aI0', particles.sparks, 4, 16, 0, 1],
        ['aI1', particles.sparks, 4, 16, 4, 1],
        ['aI2', particles.sparks, 4, 16, 8, 1],
        ['aI3', particles.sparks, 4, 16, 12, 1],
      ])
      gl.uniform1f(u('uTau'), tau)
      gl.uniform1f(u('uTrail'), trail)
      gl.uniform1f(u('uMin'), sizeOf(viewport))
      gl.uniform1f(u('uK'), TRAIL_SAMPLES)
      gl.uniform1f(u('uDayK'), dayK)
      gl.uniform1f(u('uBackA'), motion.backA)
      gl.uniform1f(u('uPow'), motion.expandPow)
      gl.uniform1f(u('uDot'), DOT_SIZE)
      gl.uniform2f(u('uCC'), viewport.width / 2, viewport.height / 2)
      gl.uniform4f(u('uSpin'), motion.lamEnd, motion.spin, motion.dolly[0], motion.dolly[1])
      gl.uniform4f(u('uGlobe'), motion.r0, motion.r1, Math.cos(motion.tilt), Math.sin(motion.tilt))
      gl.uniform4f(u('uLight'), light.lx, light.ly, light.se, light.ce)
      gl.uniform3fv(
        u('uCols[0]'),
        COLS.flatMap((c) => unit(c))
      )
      gpu.drawInstanced(globe.ribbonVertices, count)
    },

    /** Film grain over everything. */
    grain(globe: GlobeResources, alpha: number, frame: FrameState) {
      const program = gpu.program('grain')
      function u(name: string) {
        return gpu.uniform(program, name)
      }
      gpu.use(program, frame.viewport)
      gpu.blend('grain')
      gpu.bind(program, [['aQ', globe.screen, 2, 2, 0]])
      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(gl.TEXTURE_2D, globe.grain)
      gl.uniform1i(u('uTex'), 0)
      gl.uniform2f(u('uOff'), (Math.random() * 256) | 0, (Math.random() * 256) | 0)
      gl.uniform1f(u('uA'), alpha)
      gl.drawArrays(gl.TRIANGLES, 0, 3)
    },
  }

  function camera(program: Program, v: View, frame: FrameState) {
    function u(name: string) {
      return gpu.uniform(program, name)
    }
    const { light, shade } = frame
    gl.uniform2f(u('uC'), v.cx, v.cy)
    gl.uniform1f(u('uRs'), v.Rs)
    gl.uniform1f(u('uD'), v.d)
    gl.uniform1f(u('uF'), v.Rs * Math.sqrt(v.d * v.d - 1))
    gl.uniform1f(u('uZc'), 1 / v.d)
    gl.uniform4f(u('uRot'), Math.sin(v.lam), Math.cos(v.lam), Math.cos(v.tilt), Math.sin(v.tilt))
    gl.uniform4f(u('uLight'), light.lx, light.ly, light.se, light.ce)
    const near = u('uNear[0]')
    if (near) {
      gl.uniform4fv(near, frame.near)
      gl.uniform1f(u('uSig2'), NEAR_SIGMA * NEAR_SIGMA)
    }
    gl.uniform4f(
      u('uShade'),
      Math.min(1, SHADE_A * Math.abs(shade.w)),
      shade.w >= 0 ? 1 : -1,
      shade.x,
      Math.max(SHADE_SPAN * frame.viewport.width, 1)
    )
    gl.uniform1f(u('uShadeK'), SHADE_CURVE)
  }

  function sizeOf(v: Viewport) {
    return Math.min(v.width, v.height)
  }
}

export type Layers = ReturnType<typeof createLayers>

function unit([r, g, b]: Rgb): Rgb {
  return [r / 255, g / 255, b / 255]
}
